// Shared collaborative-sync primitives, used by both FlowchartBuilder's and
// MindmapBuilder's save/autosave/child-mindmap-link paths. Before this file
// existed, the same "optimistic-locking write, on conflict fetch+merge+retry"
// control flow was independently reimplemented four times (flowchart autosave,
// mindmap autosave, mindmap manual save, mindmap child-link save), and the same
// realtime "should I even apply this incoming update" guard logic was
// reimplemented twice — with the flowchart and mindmap copies drifting out of
// sync as bugs were found and fixed in only one of them. These are pure
// functions (no Supabase client, no React) precisely so they're unit-testable
// and so every caller gets the same fix at the same time.

/**
 * Decides what to do with an incoming realtime update, before any merge work
 * happens. `equals` defaults to a JSON.stringify comparison; pass your own for
 * a cheaper or more precise comparison if the content shape allows it.
 *
 * - 'ignore': a stale/duplicate/echo/older-than-our-last-save message, or one
 *   for a different document than `currentId` (e.g. the user switched away
 *   right as it arrived) — don't touch state at all.
 * - 'metadata-only': the content itself (boxes/nodes/etc) is byte-identical to
 *   what we already have locally, so this was a metadata-only write (a view
 *   setting, a gantt option, ...) that still broadcasts the whole row. Skip
 *   the merge machinery; the caller applies only the non-content fields it
 *   cares about (e.g. gantt_start_date) and should NOT show a "changes made
 *   by" toast — updated_by_email on a metadata-only write still names whoever
 *   made the last REAL content edit, unrelated to this update.
 * - 'apply': a genuine content change from elsewhere; run the real merge.
 */
export type RealtimeUpdateDecision = 'ignore' | 'metadata-only' | 'apply';

export function evaluateRealtimeUpdate<TContent>(params: {
  incomingId?: string | null;
  currentId: string;
  incomingContent: TContent;
  lastRemoteContent: TContent | null;
  lastSyncedContent: TContent | null;
  currentLocalContent: TContent;
  updatedAt?: string | null;
  lastLocalSaveTime: number;
  equals?: (a: TContent, b: TContent) => boolean;
}): RealtimeUpdateDecision {
  const equals = params.equals ?? ((a: TContent, b: TContent) => JSON.stringify(a) === JSON.stringify(b));

  // Defense in depth: the realtime channel is scoped to one document id and torn
  // down the moment currentId changes, which normally makes this impossible —
  // but that teardown and this evaluation happening against the latest render
  // are both async relative to the browser's message queue, so ruling it out
  // explicitly is cheap insurance against a message for a document we've since
  // navigated away from.
  if (params.incomingId && params.incomingId !== params.currentId) return 'ignore';

  if (params.lastRemoteContent && equals(params.lastRemoteContent, params.incomingContent)) return 'ignore';

  // Echo guard: this is exactly what we last wrote ourselves.
  if (params.lastSyncedContent && equals(params.lastSyncedContent, params.incomingContent)) return 'ignore';

  // Timestamp guard: older than our last local save minus a grace window —
  // another (possibly idle) client's stale data must not clobber our recent edit.
  if (params.updatedAt && params.lastLocalSaveTime > 0) {
    const remoteTime = new Date(params.updatedAt).getTime();
    if (remoteTime < params.lastLocalSaveTime - 3000) return 'ignore';
  }

  if (equals(params.currentLocalContent, params.incomingContent)) return 'metadata-only';

  return 'apply';
}

/**
 * Runs one optimistic-locking write attempt and, on a version conflict, fetches
 * the fresh row and merges (base, local, fresh) via the caller-supplied `merge`
 * — but does NOT retry the write itself. Callers that want the merged result
 * persisted right away (a manual "Save" click) call `write` again themselves
 * with the merged content; callers on a periodic/debounced cycle (autosave) can
 * just apply `mergedContent` to local state and let the next tick save it.
 */
export type ConflictRetryOutcome<TContent> =
  | { outcome: 'written'; version: number; updatedAt: string }
  | { outcome: 'merged'; version: number; mergedContent: TContent }
  | { outcome: 'empty-remote-protected'; version: number }
  | { outcome: 'no-real-conflict' }
  | { outcome: 'error'; error: unknown };

export async function writeWithConflictMerge<TContent, TFreshRow extends { version: number }>(params: {
  write: () => Promise<{ data: { updated_at: string; version: number } | null; error: unknown }>;
  expectedVersion: number;
  fetchFresh: () => Promise<{ data: TFreshRow | null; error?: unknown }>;
  parseFresh: (row: TFreshRow) => TContent;
  isEmpty: (content: TContent) => boolean;
  hasLocalContent: boolean;
  base: TContent | null;
  local: TContent;
  merge: (base: TContent | null, local: TContent, fresh: TContent) => TContent;
}): Promise<ConflictRetryOutcome<TContent>> {
  const { data: saved, error } = await params.write();
  if (error) return { outcome: 'error', error };
  if (saved) return { outcome: 'written', version: saved.version, updatedAt: saved.updated_at };

  // 0 rows updated — either a real version conflict, or the write matched no
  // row for some other reason (e.g. no write permission on this document).
  const { data: fresh, error: fetchError } = await params.fetchFresh();
  if (fetchError || !fresh) return { outcome: 'error', error: fetchError };
  if (fresh.version === params.expectedVersion) return { outcome: 'no-real-conflict' };

  const freshContent = params.parseFresh(fresh);
  if (params.hasLocalContent && params.isEmpty(freshContent)) {
    // A genuinely empty remote state must never wipe a populated local one —
    // only adopt the version, so the next write attempt saves our own content
    // under it instead of conflicting again.
    return { outcome: 'empty-remote-protected', version: fresh.version };
  }
  const mergedContent = params.merge(params.base, params.local, freshContent);
  return { outcome: 'merged', version: fresh.version, mergedContent };
}
